import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Country } from "@linkfluence/models/country";
import { Locale } from "@linkfluence/models/locale";
import { useSize } from "@lkf/hooks";
import { Variant } from "./colors";
import Footer from "./footer";
import Layout from "./layout";
import { GeoData, Mode } from "./model";
import Tooltip from "./tooltip";

const Map = React.lazy(() => import("./map"));

export interface IProps<D extends GeoData> {
  data: D[];
  geoJsonWorldUrl: string;
  geoJsonEndpoint: string;
  styleUrl: string;
  accessToken: string;
  locale: Locale;
  onClick?: (data: D["id"]) => void;
  colors?: string[];
  variant?: Variant;
  tooltip?: (data: D) => React.ReactNode;
  country?: Country;
  mode?: Mode;
  populations?: boolean;
}

const TooltipContainer = styled.div`
  cursor: pointer;
`;

interface HoveredData {
  data: GeoData;
  event: mapboxgl.MapMouseEvent & mapboxgl.EventData;
}

interface AutosizeTooltipProps {
  width: number;
  height: number;
  hoveredData: HoveredData;
  children: React.ReactNode;
}

const AutosizeTooltip = ({
  width,
  height,
  hoveredData,
  children,
}: AutosizeTooltipProps) => {
  const tooltipContent = useRef();
  const { width: tooltipWidth, height: tooltipHeight } = useSize(
    tooltipContent
  );
  return (
    <Tooltip
      x={hoveredData ? hoveredData.event.point.x : null}
      y={hoveredData ? hoveredData.event.point.y : null}
      width={width}
      height={height}
      tooltipWidth={tooltipWidth}
      tooltipHeight={tooltipHeight}
      ref={tooltipContent}
    >
      <div style={{ padding: children ? 12 : 0 }}>{children}</div>
    </Tooltip>
  );
};

function Component<D extends GeoData>({
  data,
  locale,
  colors,
  variant,
  tooltip,
  mode = "countries",
  populations,
  ...props
}: IProps<D>) {
  const mapWrapper = useRef<HTMLDivElement>();
  const tooltipContainer = useRef<HTMLDivElement>();
  const [hoveredData, setHoveredData] = useState<HoveredData>(null);
  const [loaded, setIsLoaded] = useState(false);
  const { width, height } = useSize(mapWrapper);
  return (
    <Layout
      mapWrapper={mapWrapper}
      map={
        <React.Suspense fallback={<span />}>
          <Map
            setIsLoaded={setIsLoaded}
            setHoveredData={setHoveredData}
            data={data}
            colors={colors}
            variant={variant}
            mode={mode}
            populations={populations}
            mapWrapper={mapWrapper}
            tooltipContainer={tooltipContainer}
            {...props}
          />
        </React.Suspense>
      }
      footer={
        !!data && (
          <Footer
            hoveredData={hoveredData}
            locale={locale}
            colors={colors}
            variant={variant}
            mode={mode}
            populations={populations}
            data={data}
          />
        )
      }
      loading={!loaded}
      tooltip={
        <TooltipContainer ref={tooltipContainer}>
          <AutosizeTooltip
            hoveredData={hoveredData}
            width={width}
            height={height}
          >
            {!!hoveredData && !!tooltip && tooltip(hoveredData.data as D)}
          </AutosizeTooltip>
        </TooltipContainer>
      }
    />
  );
}

export default Component;
